/**
 * cf-calc-analytics.js - Click Fencing calculator funnel tracking
 *
 * Why this exists: as of 2026-09-01 the four calculators reported NOTHING. Roughly
 * 2,375 calculator page views over 90 days produced zero measurable drop-off data,
 * so "where do customers give up" was unanswerable. There is no GTM container and
 * no dataLayer events on these pages - only Shopify's own Google tag.
 *
 * Two sinks, deliberately:
 *   1. gtag()  - window.gtag is already live (AW-18355899206 / GT-TQS6HNZZ), so this
 *                works the moment the theme publishes, with no other deploy.
 *   2. the CRM worker POST /calc-event - first-party, so ad blockers do not eat it,
 *                and it lands in the same D1 as the quotes so the funnel joins to
 *                real enquiries. Harmlessly no-ops (caught) until that route ships.
 *
 * The calculators are closed over - only glass exposes an instance - so everything
 * here is observed from the DOM and the network. It never calls into calculator
 * internals, never calls addToCart (which alerts), and every listener is wrapped so
 * a tracking bug can never break a customer's quote.
 *
 * The event that actually answers the question is `exit`: on page hide it reports the
 * furthest step reached, so every abandoned session says where it stopped.
 */
(function () {
  'use strict';

  if (window.__cfCalcAnalytics) return;
  window.__cfCalcAnalytics = true;

  var CALC_BY_PATH = {
    '/pages/glass-fencing': 'glass',
    '/pages/fence-calculator': 'glass',
    '/pages/blade-fence-calculator': 'blade',
    '/pages/flat-top-fence-calculator': 'flattop',
    '/pages/perfmesh-fence': 'perfmesh',
    '/pages/base-mount-batten-calculator': 'batten'
  };

  var path = (location.pathname || '/').replace(/\/+$/, '') || '/';
  var CALC = CALC_BY_PATH[path];
  if (!CALC) return;

  var WORKER_URL = 'https://clickfencing-crm-api.ppfencing2.workers.dev/calc-event';

  // Ordered funnel. The number is what makes `exit` readable as a drop-off point.
  var STEPS = {
    view: 1,
    start: 2,
    length_entered: 3,
    section_added: 4,
    gate_added: 5,
    price_shown: 6,
    quote_saved: 7,
    details_opened: 8,
    enquiry_submitted: 9,
    add_to_cart: 10
  };

  var startedAt = Date.now();
  var furthest = 0;
  var fired = {};
  var queue = [];
  var flushTimer = null;

  function safe(fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (err) {
        if (window.console && console.debug) console.debug('[cf-calc-analytics]', err);
      }
    };
  }

  function uid() {
    try {
      if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) {}
    return 'x' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  function stored(store, key) {
    try {
      var v = store.getItem(key);
      if (!v) {
        v = uid();
        store.setItem(key, v);
      }
      return v;
    } catch (e) {
      return 'nostore-' + uid();
    }
  }

  var visitorId = stored(window.localStorage, 'cf_vid');
  var sessionId = stored(window.sessionStorage, 'cf_sid');

  function device() {
    var w = window.innerWidth || 0;
    return w < 768 ? 'mobile' : (w < 1024 ? 'tablet' : 'desktop');
  }

  function flush(useBeacon) {
    if (!queue.length) return;
    var batch = queue.splice(0, queue.length);
    var body = JSON.stringify({ events: batch });
    try {
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(WORKER_URL, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
      } else {
        fetch(WORKER_URL, {
          method: 'POST',
          body: body,
          keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' }
        })['catch'](function () {
          // endpoint not deployed yet - by design, stay silent
        });
      }
    } catch (e) {
      // never surface anything to the customer
    }
  }

  var track = safe(function (name, detail) {
    var step = STEPS[name] || null;

    // Every step except `exit` fires at most once per page - we want the funnel shape,
    // not raw volume.
    if (name !== 'exit') {
      if (fired[name]) return;
      fired[name] = true;
      if (step && step > furthest) furthest = step;
    }

    var evt = {
      visitorId: visitorId,
      sessionId: sessionId,
      calc: CALC,
      event: name,
      step: name === 'exit' ? furthest : step,
      detail: detail === undefined ? null : detail,
      path: path,
      device: device(),
      msOnPage: Date.now() - startedAt
    };

    queue.push(evt);

    // This asset is deferred but Shopify's Google tag loads async, so window.gtag is
    // NOT reliably defined yet when the first event (`view`) fires - measured
    // 2026-09-01: view was silently dropped. Falling back to a dataLayer push keeps
    // the event; the tag replays the queue once it loads. `view` is the denominator
    // of the whole funnel, so losing it would make every rate meaningless.
    try {
      window.dataLayer = window.dataLayer || [];
      var send = typeof window.gtag === 'function'
        ? window.gtag
        : function () { window.dataLayer.push(arguments); };
      send('event', 'cf_calc_' + name, {
        cf_calculator: CALC,
        cf_step: evt.step,
        cf_furthest_step: furthest,
        cf_seconds_on_page: Math.round(evt.msOnPage / 1000),
        cf_detail: typeof detail === 'object' ? JSON.stringify(detail) : detail
      });
    } catch (e) {}

    if (name === 'exit' || name === 'enquiry_submitted' || name === 'add_to_cart') {
      flush(name === 'exit');
    } else {
      clearTimeout(flushTimer);
      flushTimer = setTimeout(function () {
        flush(false);
      }, 2000);
    }
  });

  // -- Detection ------------------------------------------------------------
  // All DOM-observed. #quote-breakdown and #section-preview do not exist until the
  // Configure Sections tab is active, so nothing here may assume they are present.

  track('view');

  document.addEventListener('input', safe(function (e) {
    var el = e.target;
    if (!el || !el.id) return;
    track('start');
    if (/-length$/.test(el.id) && parseFloat(el.value) > 0) {
      track('length_entered', { field: el.id, value: String(el.value).slice(0, 12) });
    }
  }), true);

  document.addEventListener('change', safe(function (e) {
    var el = e.target;
    if (!el) return;
    track('start');
    if (el.id === 'sections-count' && parseInt(el.value, 10) > 1) {
      track('section_added', { sections: parseInt(el.value, 10) });
    }
    if (el.id && /gate/i.test(el.id) && el.type === 'checkbox' && el.checked) {
      track('gate_added', { field: el.id });
    }
  }), true);

  document.addEventListener('click', safe(function (e) {
    var el = e.target && e.target.closest ? e.target.closest('button, a, [role="button"]') : null;
    if (!el) return;
    var label = (el.innerText || el.textContent || '').trim().toLowerCase().slice(0, 60);
    if (!label) return;
    if (/save.*quote|add.*quote|save this/.test(label)) track('quote_saved', { label: label });
    if (/get.*quote|send.*plan|enquir|your details|request/.test(label)) {
      track('details_opened', { label: label });
    }
  }), true);

  // Price appearing is the moment the customer learns the number - the single most
  // important step to know they reached.
  try {
    var priceObserver = new MutationObserver(safe(function () {
      var box = document.getElementById('quote-breakdown');
      if (!box) return;
      var txt = box.innerText || '';
      var m = txt.match(/\$\s?([\d,]+(?:\.\d{2})?)/);
      if (m && parseFloat(m[1].replace(/,/g, '')) > 0) {
        track('price_shown', { total: m[0] });
      }
    }));
    priceObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
  } catch (e) {}

  // Network-level truth for the two conversions, independent of button labels.
  try {
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) {}
      if (/\/cart\/add/.test(url)) track('add_to_cart');
      if (/\/enquiry\b/.test(url)) track('enquiry_submitted');
      return origFetch.apply(this, arguments);
    };
  } catch (e) {}

  var exited = false;
  var onExit = safe(function () {
    if (exited) return;
    exited = true;
    track('exit');
  });

  window.addEventListener('pagehide', onExit);
  window.addEventListener('beforeunload', onExit);
  document.addEventListener('visibilitychange', safe(function () {
    if (document.visibilityState === 'hidden') flush(true);
  }));

  if (window.console && console.log) {
    console.log('[cf-calc-analytics] installed for "' + CALC + '"');
  }
})();
